/**
 * The cabinet's display languages: the platform's fourteen (`language`, seeded by `0003`), each named in
 * itself so an author who cannot read English still finds theirs. Order is the table's `sort_order`.
 */
export const CABINET_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'ru', name: 'Русский' },
  { code: 'pl', name: 'Polski' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
  { code: 'ja', name: '日本語' },
  { code: 'zh', name: '中文' },
  { code: 'uk', name: 'Українська' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'ko', name: '한국어' },
  { code: 'tr', name: 'Türkçe' },
] as const;

export type CabinetLanguage = (typeof CABINET_LANGUAGES)[number]['code'];

export const DEFAULT_CABINET_LANGUAGE: CabinetLanguage = 'en';

const CODES: ReadonlySet<string> = new Set(CABINET_LANGUAGES.map((language) => language.code));

export function isCabinetLanguage(value: unknown): value is CabinetLanguage {
  return typeof value === 'string' && CODES.has(value);
}

/**
 * A language tag (`de-AT`, `pt-BR`, `zh-Hant-TW`) to one of the fourteen by its primary subtag, or
 * `null`. The platform keeps one variant per language, so the region and script are dropped.
 */
export function cabinetLanguageOf(tag: string | null | undefined): CabinetLanguage | null {
  if (typeof tag !== 'string') {
    return null;
  }
  const primary = tag.trim().toLowerCase().split(/[-_]/u)[0];
  return isCabinetLanguage(primary) ? primary : null;
}
